import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Sentry from "@sentry/react-native";
import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

export async function saveToStorage(key: string, value: string): Promise<void> {
  try {
    if (Platform.OS === "web") {
      await AsyncStorage.setItem(key, value);
    } else {
      await SecureStore.setItemAsync(key, value);
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error saving data:", error);
  }
}

export async function getValueFromStorage(key: string): Promise<string | null> {
  try {
    if (Platform.OS === "web") {
      return await AsyncStorage.getItem(key);
    }
    return await SecureStore.getItemAsync(key);
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error retrieving data:", error);
  }
  return null;
}

export async function removeFromStorage(key: string): Promise<void> {
  try {
    if (Platform.OS === "web") {
      await AsyncStorage.removeItem(key);
    } else {
      await SecureStore.deleteItemAsync(key);
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error removing data:", error);
  }
}

export async function popFromStorage(key: string): Promise<string | null> {
  try {
    const data = await getValueFromStorage(key);
    await removeFromStorage(key);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error popping data:", error);
    return null;
  }
}

export async function removeWhenLogout(): Promise<void> {
  const keysToKeep = ["cachedQr", "skin", "sessionToken", "guestRecentPlates"];

  try {
    if (Platform.OS === "web") {
      const allKeys = await AsyncStorage.getAllKeys();
      const keysToRemove = allKeys.filter((key) => !keysToKeep.includes(key));

      await AsyncStorage.multiRemove(keysToRemove);
    } else {
      for (const key of keysToKeep) {
        const value = await SecureStore.getItemAsync(key);
        await SecureStore.deleteItemAsync(key);
        if (value !== null) {
          await SecureStore.setItemAsync(key, value);
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error removing data while preserving specific keys:", error);
  }
}

// creates an array named "key" and pushes "value" to it. If the array doesn't exist, it creates one
export async function pushToStorageArray(key: string, value: string) {
  try {
    const array = await getValueFromStorage(key);
    let plates: string[] = JSON.parse(array || "[]");
    if (!plates.includes(value)) {
      plates.unshift(value);
      plates = plates.slice(0, 3);
      saveToStorage(key, JSON.stringify(plates));
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error pushing data to local storage: ", error);
    return null;
  }
}

export async function popFromStorageArray(key: string, value: string) {
  try {
    const array = await getValueFromStorage(key);
    let plates: string[] = JSON.parse(array || "[]");
    plates = plates.filter((plate) => plate !== value);
    saveToStorage(key, JSON.stringify(plates));
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error removing data from local storage: ", error);
    return null;
  }
}

export async function getStorageArrayLength(key: string): Promise<number> {
  try {
    const array = await getValueFromStorage(key);
    const plates: string[] = JSON.parse(array || "[]");
    return plates.length;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error getting storage array length: ", error);
    return 0;
  }
}
